import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Alert, Box, Button, Card, Chip, CircularProgress,
    Grid, IconButton, MenuItem, Stack, Table, TableBody,
    TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";
import { BarChart } from "@mui/x-charts/BarChart";
import { LineChart } from "@mui/x-charts/LineChart";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import PersonIcon from "@mui/icons-material/Person";
import AssignmentLateIcon from "@mui/icons-material/AssignmentLate";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import FilterListIcon from "@mui/icons-material/FilterList";

import { useAuditUser } from "../hooks/useAuditUser";
import {
    getStatsResumen, getStatsEvolucion,
    getStatsControladores, getStatsVencidos,
    getControladores, getAdvertencias,
} from "../services/audit.service";

// ── helper: año desde endpoint stats/anios no existe aun, los calculamos del back ─
const PERIODOS = [
    { value: "1m", label: "Último mes" },
    { value: "3m", label: "Últimos 3 meses" },
    { value: "6m", label: "Últimos 6 meses" },
    { value: "1y", label: "Último año" },
    { value: "all", label: "Todo el historial" },
];

const fmt = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("es-AR", {
        day: "2-digit", month: "2-digit", year: "numeric",
    });
};

const fmtMes = (m) => {
    if (!m) return "";
    const [y, mo] = m.split("-");
    return new Date(y, mo - 1).toLocaleDateString("es-AR", { month: "short", year: "2-digit" });
};

const PRIORIDAD_COLOR = {
    BAJA: "#22c55e", MEDIA: "#f59e0b", ALTA: "#f97316", CRITICA: "#ef4444",
};

// ── Export CSV ────────────────────────────────────────────────
function exportCSV(data, filename) {
    if (!data?.length) return;
    const headers = Object.keys(data[0]);
    const rows = data.map(r => headers.map(h => `"${r[h] ?? ""}"`).join(","));
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
}

// ── KPI Card ──────────────────────────────────────────────────
function KpiCard({ label, value, color, icon, sub }) {
    return (
        <Card sx={{ flex: 1, minWidth: 130 }}>
            <Box sx={{ p: "12px 16px" }}>
                <Stack direction="row" alignItems="center" gap={1.5}>
                    <Box sx={{
                        width: 38, height: 38, borderRadius: 2,
                        backgroundColor: `${color}18`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        color, flexShrink: 0,
                    }}>
                        {React.cloneElement(icon, { sx: { fontSize: 18 } })}
                    </Box>
                    <Box>
                        <Typography variant="h5" fontWeight={800} color={color} lineHeight={1}>
                            {value ?? "—"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{label}</Typography>
                        {sub && <Typography variant="caption" color="text.disabled" display="block">{sub}</Typography>}
                    </Box>
                </Stack>
            </Box>
        </Card>
    );
}

// ── Section title ─────────────────────────────────────────────
function SectionTitle({ icon, label, action }) {
    return (
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
            <Stack direction="row" alignItems="center" gap={1}>
                <Box sx={{ color: "primary.main", display: "flex" }}>
                    {React.cloneElement(icon, { sx: { fontSize: 16 } })}
                </Box>
                <Typography variant="overline">{label}</Typography>
            </Stack>
            {action}
        </Stack>
    );
}

// ── Años disponibles (generados en el front desde hoy hacia 3 años atrás) ──
function getAniosDisponibles() {
    const actual = new Date().getFullYear();
    return [actual, actual - 1, actual - 2, actual - 3];
}

// ══════════════════════════════════════════════════════════════
export default function AuditStats() {
    const navigate = useNavigate();
    const { username, puedeVerTodo } = useAuditUser();

    // ── Filtros ───────────────────────────────────────────────
    const [filtros, setFiltros] = useState({
        periodo: "all",
        anio: "",
        controlador_username: "",
        advertencia_id: "",
    });
    const [listaControladores, setListaControladores] = useState([]);
    const [listaAdvertencias, setListaAdvertencias] = useState([]);

    // ── Datos ─────────────────────────────────────────────────
    const [resumen, setResumen] = useState(null);
    const [evolucion, setEvolucion] = useState([]);
    const [controladores, setControladores] = useState([]);
    const [vencidos, setVencidos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Cargar listas de selects
    useEffect(() => {
        getControladores().then(setListaControladores).catch(() => { });
        getAdvertencias().then(setListaAdvertencias).catch(() => { });
    }, []);

    const cargar = useCallback(async () => {
        setLoading(true);
        setError(null);
        const params = {
            username,
            ...(filtros.anio
                ? { anio: filtros.anio }
                : filtros.periodo !== "all" ? { periodo: filtros.periodo } : {}),
            ...(filtros.controlador_username ? { controlador_username: filtros.controlador_username } : {}),
            ...(filtros.advertencia_id ? { advertencia_id: filtros.advertencia_id } : {}),
        };
        try {
            const [r, e, c, v] = await Promise.all([
                getStatsResumen(params),
                getStatsEvolucion(params),
                getStatsControladores(params),
                getStatsVencidos(params),
            ]);
            setResumen(r);
            setEvolucion(e);
            setControladores(c);
            setVencidos(v);
        } catch {
            setError("Error al cargar las estadísticas.");
        } finally {
            setLoading(false);
        }
    }, [username, filtros]);

    useEffect(() => { cargar(); }, [cargar]);

    const handleFiltro = (e) => {
        const { name, value } = e.target;
        setFiltros(p => ({
            ...p,
            [name]: value,
            // Si elige año, limpia periodo y viceversa
            ...(name === "anio" && value ? { periodo: "all" } : {}),
            ...(name === "periodo" && value !== "all" ? { anio: "" } : {}),
        }));
    };

    const limpiarFiltros = () => setFiltros({ periodo: "all", anio: "", controlador_username: "", advertencia_id: "" });

    if (!puedeVerTodo) return (
        <Box sx={{ p: 3 }}>
            <Alert severity="error">Sin acceso a estadísticas.</Alert>
        </Box>
    );

    const kpi = resumen?.kpis || {};
    const pieEstado = (resumen?.por_estado || []).map((e, i) => ({
        id: i, value: Number(e.cantidad), label: e.descripcion, color: e.color,
    }));
    const barPrioridad = (resumen?.por_prioridad || []).map(e => e.prioridad);
    const barPrioridadVals = (resumen?.por_prioridad || []).map(e => Number(e.cantidad));
    const barPrioridadColors = (resumen?.por_prioridad || []).map(e => PRIORIDAD_COLOR[e.prioridad] || "#94a3b8");
    const sucursales = (resumen?.por_sucursal || []).map(s => s.sucursal);
    const sucAbiertos = (resumen?.por_sucursal || []).map(s => Number(s.abiertos));
    const sucCerrados = (resumen?.por_sucursal || []).map(s => Number(s.cerrados));
    const advChartLabels = (resumen?.por_advertencia || []).map(a => a.advertencia);
    const advChartAbiertos = (resumen?.por_advertencia || []).map(a => Number(a.abiertos));
    const advChartCerrados = (resumen?.por_advertencia || []).map(a => Number(a.cerrados));
    const evoMeses = evolucion.map(e => fmtMes(e.mes));
    const evoAbiertos = evolucion.map(e => Number(e.abiertos));
    const evoCerrados = evolucion.map(e => Number(e.cerrados));

    // KPI advertencia más frecuente (con más abiertos)
    const advMasFrecuente = (resumen?.por_advertencia || [])
        .sort((a, b) => Number(b.abiertos) - Number(a.abiertos))[0];

    // Etiqueta resumen del filtro activo
    const filtroLabel = [
        filtros.anio ? `Año ${filtros.anio}` : "",
        filtros.periodo !== "all" && !filtros.anio
            ? PERIODOS.find(p => p.value === filtros.periodo)?.label : "",
        filtros.controlador_username
            ? `Controlador: ${listaControladores.find(c => c.username === filtros.controlador_username)?.nombre_completo || filtros.controlador_username}` : "",
        filtros.advertencia_id
            ? `Advertencia: ${listaAdvertencias.find(a => a.id == filtros.advertencia_id)?.nombre || filtros.advertencia_id}` : "",
    ].filter(Boolean).join(" · ");

    return (
        <Box sx={{ p: { xs: 1.5, md: 2.5 }, backgroundColor: "background.default", minHeight: "100vh" }}>

            {/* ── Header ── */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Stack direction="row" alignItems="center" gap={1}>
                    <IconButton size="small" onClick={() => navigate(-1)}>
                        <ArrowBackIcon fontSize="small" />
                    </IconButton>
                    <Box>
                        <Typography variant="h5">Estadísticas</Typography>
                        <Typography variant="caption" color="text.secondary">
                            Panel de análisis — módulo de auditoría
                        </Typography>
                    </Box>
                </Stack>
                <Tooltip title="Actualizar">
                    <IconButton onClick={cargar} disabled={loading}>
                        <RefreshIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Stack>

            {/* ── Panel de filtros ── */}
            <Card sx={{ mb: 2 }}>
                <Box sx={{ p: "10px 16px" }}>
                    <Stack direction="row" alignItems="center" gap={1} mb={1}>
                        <FilterListIcon sx={{ fontSize: 15, color: "primary.main" }} />
                        <Typography variant="overline">Filtrar estadísticas</Typography>
                        {filtroLabel && (
                            <Chip label={filtroLabel} size="small" onDelete={limpiarFiltros}
                                sx={{ backgroundColor: "#eff6ff", color: "#1d4ed8", fontWeight: 500 }} />
                        )}
                    </Stack>
                    <Grid container spacing={1.5} alignItems="flex-end">
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField select fullWidth label="Período" name="periodo"
                                value={filtros.periodo} onChange={handleFiltro}
                                disabled={!!filtros.anio}>
                                {PERIODOS.map(p => (
                                    <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <TextField select fullWidth label="Año" name="anio"
                                value={filtros.anio} onChange={handleFiltro}>
                                <MenuItem value="">Todos los años</MenuItem>
                                {getAniosDisponibles().map(a => (
                                    <MenuItem key={a} value={a}>{a}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField select fullWidth label="Controlador" name="controlador_username"
                                value={filtros.controlador_username} onChange={handleFiltro}>
                                <MenuItem value="">Todos los controladores</MenuItem>
                                {listaControladores.map(c => (
                                    <MenuItem key={c.username} value={c.username}>{c.nombre_completo}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField select fullWidth label="Tipo de advertencia" name="advertencia_id"
                                value={filtros.advertencia_id} onChange={handleFiltro}>
                                <MenuItem value="">Todas las advertencias</MenuItem>
                                {listaAdvertencias.map(a => (
                                    <MenuItem key={a.id} value={a.id}>{a.nombre}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <Button variant="text" color="inherit" size="small" onClick={limpiarFiltros}>
                                Limpiar filtros
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
            </Card>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", pt: 6 }}>
                    <CircularProgress size={32} />
                </Box>
            ) : (
                <>
                    {/* ══ KPIs ══ */}
                    <Stack direction="row" gap={1.5} mb={2} flexWrap="wrap">
                        <KpiCard label="Total ítems" value={kpi.total} color="#2563eb" icon={<TrendingUpIcon />} />
                        <KpiCard label="Abiertos" value={kpi.abiertos} color="#f59e0b" icon={<HourglassEmptyIcon />} />
                        <KpiCard label="Cerrados" value={kpi.cerrados} color="#22c55e" icon={<CheckCircleIcon />} />
                        <KpiCard label="Vencidos" value={kpi.vencidos} color="#ef4444" icon={<WarningAmberIcon />} />
                        <KpiCard label="Sin asignar" value={kpi.sin_asignar} color="#94a3b8" icon={<PersonIcon />} />
                        <KpiCard
                            label="Prom. resolución"
                            value={kpi.promedio_dias_resolucion ? `${kpi.promedio_dias_resolucion}d` : "—"}
                            color="#7c3aed" icon={<AssignmentLateIcon />} sub="días promedio"
                        />
                        {advMasFrecuente && (
                            <KpiCard
                                label="Advertencia más frecuente"
                                value={advMasFrecuente.abiertos}
                                color="#f97316"
                                icon={<WarningAmberIcon />}
                                sub={advMasFrecuente.advertencia}
                            />
                        )}
                    </Stack>

                    {/* ══ Estado + Prioridad ══ */}
                    <Grid container spacing={2} mb={2}>
                        <Grid item xs={12} md={5}>
                            <Card sx={{ height: "100%" }}>
                                <Box sx={{ p: "12px 16px" }}>
                                    <SectionTitle icon={<TrendingUpIcon />} label="Por estado" />
                                    {pieEstado.length > 0 ? (
                                        <PieChart
                                            series={[{ data: pieEstado, innerRadius: 50, outerRadius: 85, paddingAngle: 2, cornerRadius: 3 }]}
                                            height={220}
                                            slotProps={{ legend: { direction: "column", position: { vertical: "middle", horizontal: "right" }, itemMarkWidth: 10, itemMarkHeight: 10, markGap: 5, itemGap: 6, labelStyle: { fontSize: 11 } } }}
                                        />
                                    ) : <Typography variant="caption" color="text.disabled">Sin datos</Typography>}
                                </Box>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={7}>
                            <Card sx={{ height: "100%" }}>
                                <Box sx={{ p: "12px 16px" }}>
                                    <SectionTitle icon={<WarningAmberIcon />} label="Por prioridad" />
                                    {barPrioridad.length > 0 ? (
                                        <BarChart
                                            xAxis={[{ scaleType: "band", data: barPrioridad, tickLabelStyle: { fontSize: 11 } }]}
                                            yAxis={[{ tickLabelStyle: { fontSize: 11 } }]}
                                            series={[{ data: barPrioridadVals, label: "Cantidad", color: "#2563eb" }]}
                                            height={220}
                                            margin={{ top: 10, right: 10, bottom: 30, left: 30 }}
                                            slotProps={{ legend: { hidden: true } }}
                                            sx={{
                                                "& .MuiBarElement-root:nth-of-type(1)": { fill: barPrioridadColors[0] },
                                                "& .MuiBarElement-root:nth-of-type(2)": { fill: barPrioridadColors[1] },
                                                "& .MuiBarElement-root:nth-of-type(3)": { fill: barPrioridadColors[2] },
                                                "& .MuiBarElement-root:nth-of-type(4)": { fill: barPrioridadColors[3] },
                                            }}
                                        />
                                    ) : <Typography variant="caption" color="text.disabled">Sin datos</Typography>}
                                </Box>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* ══ Evolución ══ */}
                    <Card sx={{ mb: 2 }}>
                        <Box sx={{ p: "12px 16px" }}>
                            <SectionTitle
                                icon={<TrendingUpIcon />} label="Evolución mensual — últimos 12 meses"
                                action={<Button size="small" variant="outlined" startIcon={<DownloadIcon />}
                                    onClick={() => exportCSV(evolucion, "auditoria_evolucion.csv")}>CSV</Button>}
                            />
                            {evoMeses.length > 0 ? (
                                <LineChart
                                    xAxis={[{ scaleType: "point", data: evoMeses, tickLabelStyle: { fontSize: 11 } }]}
                                    yAxis={[{ tickLabelStyle: { fontSize: 11 } }]}
                                    series={[
                                        { data: evoAbiertos, label: "Abiertos", color: "#f59e0b", showMark: true },
                                        { data: evoCerrados, label: "Cerrados", color: "#22c55e", showMark: true },
                                    ]}
                                    height={220}
                                    margin={{ top: 20, right: 20, bottom: 30, left: 30 }}
                                    slotProps={{ legend: { itemMarkWidth: 10, itemMarkHeight: 10, markGap: 5, itemGap: 12, labelStyle: { fontSize: 11 } } }}
                                />
                            ) : <Typography variant="caption" color="text.disabled">Sin datos suficientes</Typography>}
                        </Box>
                    </Card>

                    {/* ══ Sucursales ══ */}
                    <Card sx={{ mb: 2 }}>
                        <Box sx={{ p: "12px 16px" }}>
                            <SectionTitle
                                icon={<TrendingUpIcon />} label="Por sucursal (top 10)"
                                action={<Button size="small" variant="outlined" startIcon={<DownloadIcon />}
                                    onClick={() => exportCSV(resumen?.por_sucursal || [], "auditoria_sucursales.csv")}>CSV</Button>}
                            />
                            {sucursales.length > 0 ? (
                                <BarChart
                                    layout="horizontal"
                                    yAxis={[{ scaleType: "band", data: sucursales, tickLabelStyle: { fontSize: 10 } }]}
                                    xAxis={[{ tickLabelStyle: { fontSize: 11 } }]}
                                    series={[
                                        { data: sucAbiertos, label: "Abiertos", color: "#fbbf24", stack: "total" },
                                        { data: sucCerrados, label: "Cerrados", color: "#34d399", stack: "total" },
                                    ]}
                                    height={Math.max(180, sucursales.length * 30)}
                                    margin={{ top: 10, right: 20, bottom: 30, left: 90 }}
                                    slotProps={{ legend: { itemMarkWidth: 10, itemMarkHeight: 10, markGap: 5, itemGap: 12, labelStyle: { fontSize: 11 } } }}
                                />
                            ) : <Typography variant="caption" color="text.disabled">Sin datos</Typography>}
                        </Box>
                    </Card>

                    {/* ══ Por advertencia ══ */}
                    {(resumen?.por_advertencia || []).length > 0 && (
                        <Card sx={{ mb: 2 }}>
                            <Box sx={{ p: "12px 16px" }}>
                                <SectionTitle
                                    icon={<WarningAmberIcon />} label="Por tipo de advertencia"
                                    action={<Button size="small" variant="outlined" startIcon={<DownloadIcon />}
                                        onClick={() => exportCSV(resumen?.por_advertencia || [], "auditoria_advertencias.csv")}>CSV</Button>}
                                />

                                {/* Gráfico */}
                                <BarChart
                                    layout="horizontal"
                                    yAxis={[{ scaleType: "band", data: advChartLabels, tickLabelStyle: { fontSize: 10 } }]}
                                    xAxis={[{ tickLabelStyle: { fontSize: 11 } }]}
                                    series={[
                                        { data: advChartAbiertos, label: "Abiertos", color: "#f97316", stack: "total" },
                                        { data: advChartCerrados, label: "Cerrados", color: "#34d399", stack: "total" },
                                    ]}
                                    height={Math.max(180, advChartLabels.length * 34)}
                                    margin={{ top: 10, right: 20, bottom: 30, left: 200 }}
                                    slotProps={{ legend: { itemMarkWidth: 10, itemMarkHeight: 10, markGap: 5, itemGap: 12, labelStyle: { fontSize: 11 } } }}
                                />

                                {/* Tabla detallada */}
                                <Box sx={{ overflowX: "auto", mt: 2 }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ "& th": { fontWeight: 700, fontSize: "0.72rem", color: "#475569", backgroundColor: "#f8fafc" } }}>
                                                <TableCell>Tipo de advertencia</TableCell>
                                                <TableCell align="center">Total</TableCell>
                                                <TableCell align="center">Abiertos</TableCell>
                                                <TableCell align="center">Cerrados</TableCell>
                                                <TableCell align="center">Vencidos</TableCell>
                                                <TableCell align="center">Prom. días</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {(resumen?.por_advertencia || []).map((a, i) => (
                                                <TableRow key={i} sx={{ "&:hover": { backgroundColor: "grey.50" } }}>
                                                    <TableCell>
                                                        <Typography variant="body2" fontWeight={500}>{a.advertencia}</Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Typography variant="body2" fontWeight={700}>{a.cantidad}</Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Chip label={a.abiertos} size="small"
                                                            sx={{ backgroundColor: "#fff7ed", color: "#c2410c", fontWeight: 700, minWidth: 32 }} />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Chip label={a.cerrados} size="small"
                                                            sx={{ backgroundColor: "#f0fdf4", color: "#166534", fontWeight: 700, minWidth: 32 }} />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Chip label={a.vencidos} size="small" sx={{
                                                            backgroundColor: Number(a.vencidos) > 0 ? "#fef2f2" : "#f8fafc",
                                                            color: Number(a.vencidos) > 0 ? "#b91c1c" : "#94a3b8",
                                                            fontWeight: 700, minWidth: 32,
                                                        }} />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Typography variant="body2"
                                                            color={Number(a.promedio_dias) > 30 ? "error.main" : "text.primary"}>
                                                            {a.promedio_dias ? `${a.promedio_dias}d` : "—"}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Box>
                            </Box>
                        </Card>
                    )}

                    {/* ══ Controladores ══ */}
                    <Card sx={{ mb: 2 }}>
                        <Box sx={{ p: "12px 16px" }}>
                            <SectionTitle
                                icon={<PersonIcon />} label="Resumen por controlador"
                                action={<Button size="small" variant="outlined" startIcon={<DownloadIcon />}
                                    onClick={() => exportCSV(controladores, "auditoria_controladores.csv")}>CSV</Button>}
                            />
                            <Box sx={{ overflowX: "auto" }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ "& th": { fontWeight: 700, fontSize: "0.72rem", color: "#475569", backgroundColor: "#f8fafc" } }}>
                                            <TableCell>Controlador</TableCell>
                                            <TableCell align="center">Asignados</TableCell>
                                            <TableCell align="center">Resueltos</TableCell>
                                            <TableCell align="center">Pendientes</TableCell>
                                            <TableCell align="center">Vencidos</TableCell>
                                            <TableCell align="center">Derivados</TableCell>
                                            <TableCell align="center">Prom. días</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {!controladores.length ? (
                                            <TableRow><TableCell colSpan={7} align="center">
                                                <Typography variant="caption" color="text.disabled">Sin datos</Typography>
                                            </TableCell></TableRow>
                                        ) : controladores.map((c, i) => (
                                            <TableRow key={i} sx={{ "&:hover": { backgroundColor: "grey.50" } }}>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight={500}>{c.nombre || c.username}</Typography>
                                                    <Typography variant="caption" color="text.disabled">{c.username}</Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Typography variant="body2" fontWeight={700}>{c.total_asignados}</Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Chip label={c.resueltos} size="small"
                                                        sx={{ backgroundColor: "#f0fdf4", color: "#166534", fontWeight: 700, minWidth: 32 }} />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Chip label={c.pendientes} size="small"
                                                        sx={{ backgroundColor: "#fff7ed", color: "#c2410c", fontWeight: 700, minWidth: 32 }} />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Chip label={c.vencidos} size="small" sx={{
                                                        backgroundColor: c.vencidos > 0 ? "#fef2f2" : "#f8fafc",
                                                        color: c.vencidos > 0 ? "#b91c1c" : "#94a3b8",
                                                        fontWeight: 700, minWidth: 32,
                                                    }} />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Typography variant="body2" color="text.secondary">{c.derivados}</Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Typography variant="body2"
                                                        color={c.promedio_dias > 30 ? "error.main" : "text.primary"}>
                                                        {c.promedio_dias ? `${c.promedio_dias}d` : "—"}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        </Box>
                    </Card>

                    {/* ══ Vencidos ══ */}
                    <Card>
                        <Box sx={{ p: "12px 16px" }}>
                            <SectionTitle
                                icon={<AssignmentLateIcon />} label={`Ítems vencidos sin cerrar (${vencidos.length})`}
                                action={<Button size="small" variant="outlined" startIcon={<DownloadIcon />}
                                    onClick={() => exportCSV(vencidos, "auditoria_vencidos.csv")}>CSV</Button>}
                            />
                            <Box sx={{ overflowX: "auto" }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ "& th": { fontWeight: 700, fontSize: "0.72rem", color: "#475569", backgroundColor: "#f8fafc" } }}>
                                            <TableCell>#</TableCell>
                                            <TableCell>Título</TableCell>
                                            <TableCell>Sucursal</TableCell>
                                            <TableCell>Prioridad</TableCell>
                                            <TableCell>Estado</TableCell>
                                            <TableCell>Controlador</TableCell>
                                            <TableCell>Límite</TableCell>
                                            <TableCell align="center">Días demora</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {!vencidos.length ? (
                                            <TableRow><TableCell colSpan={8} align="center">
                                                <Typography variant="caption" color="text.disabled">No hay ítems vencidos 🎉</Typography>
                                            </TableCell></TableRow>
                                        ) : vencidos.map((v) => (
                                            <TableRow key={v.id} onClick={() => navigate(`../${v.id}`)}
                                                sx={{ cursor: "pointer", "&:hover": { backgroundColor: "#fff5f5" } }}>
                                                <TableCell>
                                                    <Typography variant="caption" color="text.disabled" fontWeight={700}>#{v.id}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>{v.titulo}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" color="text.secondary">{v.sucursal_nombre}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip label={v.prioridad} size="small" sx={{
                                                        backgroundColor: `${PRIORIDAD_COLOR[v.prioridad]}20`,
                                                        color: PRIORIDAD_COLOR[v.prioridad], fontWeight: 700,
                                                    }} />
                                                </TableCell>
                                                <TableCell>
                                                    <Chip label={v.status_desc} size="small"
                                                        sx={{ backgroundColor: v.status_color, color: "#fff", fontWeight: 600 }} />
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" noWrap>
                                                        {v.controlador_nombre || <em style={{ color: "#94a3b8" }}>Sin asignar</em>}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" color="error.main" fontWeight={600}>
                                                        {fmt(v.fecha_requerida)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Chip label={`+${v.dias_demora}d`} size="small" sx={{
                                                        backgroundColor: v.dias_demora > 14 ? "#fef2f2" : "#fff7ed",
                                                        color: v.dias_demora > 14 ? "#b91c1c" : "#c2410c", fontWeight: 700,
                                                    }} />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        </Box>
                    </Card>
                </>
            )}
        </Box>
    );
}